// @flow
import { useEffect, useState } from 'react';
import useAxios from '@hooks/useAxios';
import { stylesheet } from 'typestyle';
import classnames from 'classnames';
import { formatPrice } from '@src/utils/general';

import ProgressiveImage from '../ProgressiveImage';
import QuestionMarkPopup from '@components/popups/QuestionMark';
import * as MatchingBandContent from '@components/popups/content/MatchingBand';
import ErrorBoundary from '@components/ErrorBoundary';

import * as bandRoutes from '@src/app_routes/bands';

import { linkBlueText, regularYellowText } from '@styles/general';

import type { TSettingDetail, SettingImageType } from '@types/setting';
import type { Node } from 'react';

type TProps = {
  setting: TSettingDetail
};

const css = stylesheet({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: 30
  },
  imageContainer: {
    flex: '1 1 calc((100% - 40px) / 3)',
    textAlign: 'center',

    $nest: {
      '& p': {
        marginBottom: 0
      },
      '& p, &a': {
        fontSize: 14
      },
      '& img': {
        maxWidth: 80
      }
    }
  },
  separator: {
    flex: '1 1 20px',
    paddingTop: 30,
    fontSize: 24,
    color: linkBlueText
  },
  price: {
    flexBasis: '100%',
    color: regularYellowText,
    textAlign: 'center',
    position: 'relative'
  },
  matchingBand: {
    marginTop: 20,

    $nest: {
      '& img': {
        maxWidth: 60
      },
      '& p': {
        color: linkBlueText,
        textDecoration: 'none'
      },
      '&:hover': {
        textDecoration: 'underline'
      }
    }
  }
});

function displayableImages(all: Array<SettingImageType> = []) {
  return all.filter(image => image.display);
}

function ImageContainer({ children }: { children: Node }) {
  return <div className={css.imageContainer}>{children}</div>;
}

function MatchingBandCallout({ setting }: TProps) {
  const [band, setBand] = useState(null);
  const axios = useAxios();

  const settingImage = displayableImages(setting.images)[0];
  const combinedImage = displayableImages(setting.images)[5];
  const bandImage = band && displayableImages(band.images)[0];

  useEffect(() => {
    {
      /* don't display if no displayable combined image exists */
    }
    if (
      !setting.matching_band_sku ||
      !combinedImage ||
      !combinedImage.display
    ) {
      return;
    }

    axios
      .get(bandRoutes.apiDetail(setting.matching_band_sku))
      .then(({ data }) => setBand(data));
  }, [setting.matching_band_sku, axios, combinedImage]);

  useEffect(() => {
    // it is possible to have stale band in memory when dragging image slider,
    // so we should clear it
    if (!setting.matching_band_sku || !combinedImage) setBand(null);
  }, [setting.matching_band_sku, setBand, combinedImage]);

  if (band && settingImage && bandImage && combinedImage) {
    return (
      <div className={css.container}>
        <ImageContainer>
          <ProgressiveImage
            images={[
              { src: settingImage.thumbnail, alt: 'Setting' },
              { src: settingImage.standard, alt: 'Setting' }
            ]}
          />
          <p>This Ring</p>
        </ImageContainer>
        <span className={css.separator}>+</span>
        <a
          className={classnames(css.imageContainer, css.matchingBand)}
          href={bandRoutes.detailPath(
            {
              vendor_sku: band.vendor_sku,
              url_segment_description: band.url_segment_description
            },
            { matching_sku: setting.vendor_sku }
          )}
          title='See matching band'
        >
          <ProgressiveImage
            images={[
              { src: bandImage.thumbnail, alt: 'See matching band' },
              { src: bandImage.standard, alt: 'See matching band' }
            ]}
            style={{ cursor: 'pointer' }}
          />
          <p>Matching Band</p>
        </a>
        <span className={css.separator}>=</span>
        <ImageContainer>
          <ProgressiveImage
            images={[
              { src: combinedImage.thumbnail, alt: 'Combined' },
              { src: combinedImage.standard, alt: 'Combined' }
            ]}
          />
          <p>A Perfect Pair</p>
        </ImageContainer>
        <div className={css.price}>
          {formatPrice(Number(band.retail_price))}

          <QuestionMarkPopup initialContent={MatchingBandContent} />
        </div>
      </div>
    );
  } else {
    return null;
  }
}

export default (props: TProps) => (
  <ErrorBoundary fallback={false}>
    <MatchingBandCallout {...props} />
  </ErrorBoundary>
);
